import React, { Component } from 'react'
import { withFirebase } from '../Fb'
import moment from 'moment'
import { Alert, Box, Card, CardContent, Grid, LinearProgress, Skeleton, Typography } from '@mui/material'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'

// The sales overview shown on Raportit before a report is picked: six periods, each against the
// period it is naturally compared with, broken down by category. Built from two busyraw reports
// (this year and last year to date), which cover every period below.

const yesterday = {
  start: moment().subtract(1, 'day'),
  end: moment().subtract(1, 'day'),
}
const yesterdayLastWeek = {
  start: moment().subtract(1, 'week').subtract(1, 'day'),
  end: moment().subtract(1, 'week').subtract(1, 'day'),
}
const last7days = {
  start: moment().subtract(7, 'day'),
  end: moment().subtract(1, 'days'),
}
const last14days = {
  start: moment().subtract(14, 'day'),
  end: moment().subtract(8, 'days'),
}
const lastWeek = {
  start: moment().subtract(1, 'week').startOf('isoWeek'),
  end: moment().subtract(1, 'week').endOf('isoWeek'),
}
const lastWeekLastYear = {
  start: moment().subtract(1, 'year').subtract(1, 'week').startOf('isoWeek'),
  end: moment().subtract(1, 'year').subtract(1, 'week').endOf('isoWeek'),
}
const lastMonth = {
  start: moment().subtract(1, 'month').startOf('month'),
  end: moment().subtract(1, 'month').endOf('month'),
}
const lastMonthLastYear = {
  start: moment().subtract(1, 'year').subtract(1, 'month').startOf('month'),
  end: moment().subtract(1, 'year').subtract(1, 'month').endOf('month'),
}
const monthBeforeLastMonth = {
  start: moment().subtract(2, 'month').startOf('month'),
  end: moment().subtract(2, 'month').endOf('month'),
}
const thisYear = {
  start: moment().startOf('year'),
  end: moment().subtract(1, 'day'),
}
const lastYear = {
  start: moment().subtract(1, 'year').startOf('year'),
  end: moment().subtract(1, 'year').subtract(1, 'day'),
}

// pairs: each period is followed by the one it is compared with
const periods = [
  { dates: yesterday, name: 'yesterday' },
  { dates: yesterdayLastWeek, name: 'yesterdayLastWeek' },
  { dates: last7days, name: 'last7days' },
  { dates: last14days, name: 'last14days' },
  { dates: lastWeek, name: 'lastWeek' },
  { dates: lastWeekLastYear, name: 'lastWeekLastYear' },
  { dates: lastMonth, name: 'lastMonth' },
  { dates: lastMonthLastYear, name: 'lastMonthLastYear' },
  { dates: lastMonth, name: 'lastMonth' },
  { dates: monthBeforeLastMonth, name: 'monthBeforeLastMonth' },
  { dates: thisYear, name: 'thisYear' },
  { dates: lastYear, name: 'lastYear' },
]

const reportperiods = [
  { dates: thisYear, name: 'thisYear' },
  { dates: lastYear, name: 'lastYear' },
]

// keyed by the comparison period, because "last month" appears twice with different comparisons
const TITLES = {
  yesterdayLastWeek: { title: 'Eilen', versus: 'vs sama päivä viikkoa aiemmin' },
  last14days: { title: 'Viimeiset 7 päivää', versus: 'vs edelliset 7 päivää' },
  lastWeekLastYear: { title: 'Viime viikko', versus: 'vs sama viikko viime vuonna' },
  lastMonthLastYear: { title: 'Viime kuukausi', versus: 'vs sama kuukausi viime vuonna' },
  monthBeforeLastMonth: { title: 'Viime kuukausi', versus: 'vs edellinen kuukausi' },
  lastYear: { title: 'Tämä vuosi', versus: 'vs sama ajanjakso viime vuonna' },
}

const eur = new Intl.NumberFormat('fi-FI', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })
const pct = new Intl.NumberFormat('fi-FI', { style: 'percent', maximumFractionDigits: 0 })

const UP = '#1e8e3e'
const DOWN = '#d93025'
const FLAT = '#6c757d'
// bar colours: this period and the period it is compared with
const CURRENT = '#51bcda'
const PREVIOUS = '#b0b7bf'

const inPeriod = (row, dates) =>
  dates.start.isSame(dates.end, 'day')
    ? row.dayIndex === dates.start.format('YYYY-MM-DD')
    : moment(row.dayIndex).isBetween(dates.start, dates.end, 'day', '[]')

// sales per category in euros
const byCategory = (rows) =>
  rows.reduce((acc, row) => {
    const key = row.category || 'muut'
    acc[key] = (acc[key] || 0) + (parseFloat(row.cost) || 0)
    return acc
  }, {})

const total = (sums) => Object.values(sums).reduce((a, b) => a + b, 0)

const splitToSums = (data) => {
  const sums = []
  for (let i = 0; i < periods.length; i += 2) {
    const period = periods[i]
    const comparison = periods[i + 1]
    sums.push({
      key: comparison.name,
      period: { sum: byCategory(data.filter((row) => inPeriod(row, period.dates))), start: period.dates.start, end: period.dates.end },
      comparisonperiod: { sum: byCategory(data.filter((row) => inPeriod(row, comparison.dates))), start: comparison.dates.start, end: comparison.dates.end },
    })
  }
  return sums
}

const range = (start, end) => (start.isSame(end, 'day') ? start.format('D.M.YYYY') : start.format('D.M.') + '–' + end.format('D.M.YYYY'))

const capitalise = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : text)

function Change({ current, previous, size }) {
  const diff = current - previous
  const direction = diff > 0 ? 'up' : diff < 0 ? 'down' : 'flat'
  const color = direction === 'up' ? UP : direction === 'down' ? DOWN : FLAT
  const text = previous > 0 ? pct.format(Math.abs(diff / previous)) : current > 0 ? 'uusi' : '–'
  const iconSize = size === 'large' ? 16 : 13
  return (
    <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, color, fontSize: size === 'large' ? 13 : 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
      {direction === 'up' ? <ArrowUpwardIcon sx={{ fontSize: iconSize }} /> : null}
      {direction === 'down' ? <ArrowDownwardIcon sx={{ fontSize: iconSize }} /> : null}
      {text}
    </Box>
  )
}

function PeriodCard({ reportKey, period, comparisonperiod }) {
  const titles = TITLES[reportKey] || { title: '', versus: '' }
  const current = total(period.sum)
  const previous = total(comparisonperiod.sum)
  const categories = Array.from(new Set([...Object.keys(period.sum), ...Object.keys(comparisonperiod.sum)]))
    .map((category) => ({ category, current: period.sum[category] || 0, previous: comparisonperiod.sum[category] || 0 }))
    .filter((c) => c.current || c.previous)
    .sort((a, b) => b.current - a.current || b.previous - a.previous)
  const biggest = Math.max(1, ...categories.map((c) => Math.max(c.current, c.previous)))

  return (
    <Card sx={{ borderRadius: 2, height: '100%', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Typography variant='caption' sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>
          {titles.title}
        </Typography>
        <Typography variant='caption' display='block' sx={{ color: 'text.secondary' }}>
          {range(period.start, period.end)}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 1, flexWrap: 'wrap' }}>
          <Box sx={{ fontSize: 28, fontWeight: 600, lineHeight: 1.15 }}>{eur.format(current)}</Box>
          <Change current={current} previous={previous} size='large' />
        </Box>
        <Typography variant='caption' sx={{ color: 'text.secondary' }}>
          {eur.format(previous)} {titles.versus} ({range(comparisonperiod.start, comparisonperiod.end)})
        </Typography>

        {categories.length === 0 ? (
          <Typography variant='body2' sx={{ color: 'text.secondary', mt: 2 }}>
            Ei myyntiä.
          </Typography>
        ) : (
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {categories.map((c) => (
              <Box key={c.category}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography variant='body2' sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {capitalise(c.category)}
                  </Typography>
                  <Typography variant='body2' sx={{ fontWeight: 600 }}>
                    {eur.format(c.current)}
                  </Typography>
                  <Box sx={{ minWidth: 64, textAlign: 'right' }}>
                    <Change current={c.current} previous={c.previous} />
                  </Box>
                </Box>
                {/* this period, and just below it the comparison period on the same scale */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <Box title={eur.format(c.current)} sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: '#eef1f4' }}>
                    <Box sx={{ height: '100%', width: (c.current / biggest) * 100 + '%', borderRadius: 3, bgcolor: CURRENT }} />
                  </Box>
                  {/* same width as the comparison amount below, so both bars share one scale */}
                  <Box sx={{ minWidth: 64 }} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <Box title={'Vertailu ' + eur.format(c.previous)} sx={{ flex: 1, height: 4, borderRadius: 2, bgcolor: '#eef1f4' }}>
                    <Box sx={{ height: '100%', width: (c.previous / biggest) * 100 + '%', borderRadius: 2, bgcolor: PREVIOUS }} />
                  </Box>
                  <Typography variant='caption' sx={{ color: 'text.secondary', minWidth: 64, textAlign: 'right', lineHeight: 1 }}>
                    {eur.format(c.previous)}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

function SkeletonCard() {
  return (
    <Card sx={{ borderRadius: 2, height: '100%', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
      <CardContent sx={{ p: 2 }}>
        <Skeleton width='40%' />
        <Skeleton width='30%' height={16} />
        <Skeleton width='55%' height={44} sx={{ mt: 1 }} />
        <Skeleton width='80%' height={16} />
        {[0, 1, 2].map((i) => (
          <Box key={i} sx={{ mt: 1.5 }}>
            <Skeleton width='100%' height={18} />
            <Skeleton variant='rounded' width={90 - i * 25 + '%'} height={6} />
          </Box>
        ))}
      </CardContent>
    </Card>
  )
}

class ReportDashboard extends Component {
  constructor(props) {
    super(props)
    this.state = { loading: true, reports: [], error: false }
  }

  updateDashboard = () => {
    Promise.all(
      reportperiods.map((period) =>
        this.props.firebase.getReport(period.dates.start.format('YYYY-MM-DD'), period.dates.end.format('YYYY-MM-DD'), 'busyraw', '00:00', '23:59')
      )
    )
      .then((response) => {
        const allrows = response.map((report) => (report.data && report.data.rows) || []).flat()
        this.setState({ loading: false, reports: splitToSums(allrows) })
      })
      .catch((error) => {
        console.error('Error getting reports', error)
        this.setState({ loading: false, error: true })
      })
  }

  componentDidMount() {
    this.updateDashboard()
  }

  render() {
    const { loading, reports, error } = this.state
    return (
      <Box sx={{ mt: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
          <Typography variant='h6' sx={{ fontWeight: 600 }}>
            Myynnin yleiskatsaus
          </Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            Varausmyynti kategorioittain. Valitse yltä raportti tarkempaa tarkastelua varten.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: { md: 'auto' }, fontSize: 12, color: 'text.secondary' }}>
            {[
              [CURRENT, 'Jakso', 6],
              [PREVIOUS, 'Vertailujakso', 4],
            ].map(([color, label, height]) => (
              <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 22, height, borderRadius: 3, bgcolor: color }} />
                {label}
              </Box>
            ))}
          </Box>
        </Box>
        {loading ? <LinearProgress sx={{ mb: 2, borderRadius: 1 }} /> : null}
        {error ? (
          <Alert severity='error' sx={{ mb: 2 }}>
            Yleiskatsauksen haku epäonnistui. Yritä myöhemmin uudelleen.
          </Alert>
        ) : null}
        <Grid container spacing={2}>
          {loading
            ? [0, 1, 2, 3, 4, 5].map((i) => (
                <Grid key={i} size={{ xs: 12, sm: 6, lg: 4 }}>
                  <SkeletonCard />
                </Grid>
              ))
            : reports.map((report) => (
                <Grid key={report.key} size={{ xs: 12, sm: 6, lg: 4 }}>
                  <PeriodCard reportKey={report.key} period={report.period} comparisonperiod={report.comparisonperiod} />
                </Grid>
              ))}
        </Grid>
      </Box>
    )
  }
}

export default withFirebase(ReportDashboard)
